/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { createSelector } from '@reduxjs/toolkit';
import { isWelcomeModalBeenActivated, isWelcomeModalForced } from '../actions/welcomeModal';
import { getRootOrg } from './initiative';
import { UserRoles } from '../constants/user';
import { RootState } from '../reducers';
import { CurrentUserData } from '../reducers/current-user';
import { InitiativePermissions } from '../services/permissions/InitiativePermissions';

export const getCurrentUser = (state: RootState): CurrentUserData | undefined => {
  return state.currentUser.loaded === true ? state.currentUser.data : undefined;
};

export const isUserLoaded = (state: RootState) => state.currentUser.loaded;

export const isUserActivated = (state: RootState) => {
  const isActive = state.currentUser.data.active;
  const hasInitiatives = Boolean(state.currentUser.data.permissions?.length ?? false);
  return isActive && hasInitiatives;
}

export const isStaff = createSelector(
  [getCurrentUser],
  (user) => user ? user.isStaff : false
)

export const canAccessCurrentLevel = (state: RootState) => {
  if (!state.currentUser.loaded || !state.initiative.loaded) {
    return false;
  }
  return InitiativePermissions.canAccess(state.currentUser.data, state.initiative.data?._id);
}
export const canManageCurrentLevel = (state: RootState) => {
  if (!state.currentUser.loaded || !state.initiative.loaded) {
    return false;
  }
  return InitiativePermissions.canManageInitiative(state.currentUser.data, state.initiative.data?._id);
}
/* @deprecated */
export const isUserManager = canManageCurrentLevel;

export const canManageInitiativeId = (state: RootState, initiativeId?: string) => {
  if (!state.currentUser.loaded || !initiativeId) {
    return false;
  }
  return InitiativePermissions.canManageInitiative(state.currentUser.data, initiativeId);
}

export const canAccessInsightsAndDownloads = (state: RootState, initiativeId?: string) => {
  if (!state.currentUser.loaded || !initiativeId) {
    return false;
  }
  return InitiativePermissions.canAccessInsightsAndDownloads(state.currentUser.data, initiativeId);
}

export const canCreateSurvey = (state: RootState) => canManageCurrentLevel(state);

export const isUserManagerByInitiativeId = (state: RootState, initiativeId?: string) => {
  if (!state.currentUser.loaded || !initiativeId) {
    return false;
  }
  const user = state.currentUser.data;
  return InitiativePermissions.canManageInitiative(user, initiativeId);
}

export const isOrgOwner = createSelector([
  getCurrentUser,
  getRootOrg,
], (user, org) => {
  if (!user || !user.permissions || !org) {
    return false;
  }

  return user.permissions.some((p) => {
    return p.initiativeId === org._id && p.permissions.includes(UserRoles.Owner);
  })
})

export const isOrgManager = createSelector([getCurrentUser, getRootOrg], (user, org) => {
  if (!user || !user.permissions || !org) {
    return false;
  }

  return user.permissions.some((p) => {
    return p.initiativeId === org._id && p.permissions.some(perm => [UserRoles.Manager, UserRoles.Owner].includes(perm));
  });
});

export const shouldTriggerWelcomeModal = createSelector(
  [
    getCurrentUser,
    isWelcomeModalBeenActivated,
    isWelcomeModalForced,
    state => state.welcomeModal.isOpen,
  ],
  (user, isActivated, isForced, isOpen) => {
    if (isForced || isOpen) {
      return true;
    }

    if (isActivated || !user) {
      return false;
    }
    // User exists, but there is no lastLogin (assumes first login)
    return (!user.lastLogin);
  });
